import React from "react";

import Input from "@editor/components/common/designSystem/Input";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useInfinitePages } from "@editor/hooks/useInfinitePages";

import ResponsiveControls from "@/features/react-editor/components/navbar/ResponsiveControls";
import Button from "@replo/design-system/components/button/Button";
import EmptyState from "@replo/design-system/components/empty-state/EmptyState";
import { MenuItem } from "@replo/design-system/components/menu/MenuItem";
import Popover from "@replo/design-system/components/popover/Popover";
import Separator from "@replo/design-system/components/Separator";
import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  ChevronDown,
  FolderSearch,
  Search,
} from "lucide-react";
import InfiniteScroll from "react-infinite-scroll-component";
import useMeasure from "react-use-measure";
import { useDebouncedState } from "replo-utils/react/use-debounced-state";

import PageCard from "./PageCard/PageCard";
import PageCardSkeleton from "./PageCard/PageCardSkeleton";

const GRID_CONSTANTS = {
  MIN_CARD_WIDTH: 300,
} as const;

const DEFAULT_ORDER: OrderState = {
  field: "updatedAt",
  direction: "desc",
};

type Field = "name" | "updatedAt" | "createdAt";
type Direction = "asc" | "desc";

type OrderState = {
  field: Field;
  direction: Direction;
};

const sortByOptions = [
  { label: "Alphabetical", value: "name" },
  { label: "Last edited", value: "updatedAt" },
  { label: "Created date", value: "createdAt" },
];

const alphabeticalSortOrderOptions = [
  { label: "A to Z", value: "asc" },
  { label: "Z to A", value: "desc" },
];

const chronologicalSortOrderOptions = [
  { label: "Newest First", value: "desc" },
  { label: "Oldest First", value: "asc" },
];

type SortDropdownProps = {
  order: OrderState;
  onOrderChange: (order: OrderState) => void;
};

const SortDropdown: React.FC<SortDropdownProps> = ({
  order,
  onOrderChange,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectableRef, { width: selectableWidth }] = useMeasure({
    offsetSize: true,
  });

  const getValueLabel = () => {
    const field = order.field;
    let label = "Sort by";

    if (field === "name") {
      label = "Alphabetical";
    } else if (field === "updatedAt") {
      label = "Last edited";
    } else if (field === "createdAt") {
      label = "Created date";
    }

    return label;
  };

  const handleSortChange = (field: Field, direction: Direction) => {
    onOrderChange({ field, direction });
  };

  return (
    <Popover.Root isOpen={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <div
          ref={selectableRef}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label="Sort options"
          className="min-w-full"
        >
          <Button
            variant="secondary"
            size="base"
            startEnhancer={
              order.direction === "asc" ? (
                <ArrowUp size={12} />
              ) : (
                <ArrowDown size={12} />
              )
            }
            endEnhancer={<ChevronDown size={12} />}
            UNSAFE_className="min-w-full text-base font-normal bg-sheet"
            titleAlignment="start"
          >
            {getValueLabel()}
          </Button>
        </div>
      </Popover.Trigger>
      <Popover.Content
        align="start"
        side="bottom"
        className="p-2"
        style={{ width: selectableWidth }}
        shouldPreventDefaultOnInteractOutside={false}
        hideCloseButton
      >
        <div className="text-default flex flex-col gap-2 text-sm">
          <div className="flex flex-col gap-1">
            <h3 className="header-h5 px-2">Sort By</h3>
            {sortByOptions.map((option) => (
              <MenuItem
                key={option.value}
                variant="default"
                size="sm"
                selected={order.field === option.value}
                onClick={() =>
                  handleSortChange(option.value as Field, order.direction)
                }
              >
                {option.label}
              </MenuItem>
            ))}
          </div>
          <Separator />
          <div className="flex flex-col gap-1">
            <h3 className="header-h5 px-2">Sort Order</h3>
            {(order.field === "name"
              ? alphabeticalSortOrderOptions
              : chronologicalSortOrderOptions
            ).map((option) => (
              <MenuItem
                key={option.value}
                variant="default"
                size="sm"
                selected={order.direction === option.value}
                onClick={() =>
                  handleSortChange(order.field, option.value as Direction)
                }
              >
                {option.label}
              </MenuItem>
            ))}
          </div>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
};

function PagesSection({
  setSelectedTab,
}: {
  setSelectedTab: (tab: "pages" | "templates") => void;
}) {
  const [order, setOrder] = React.useState<OrderState>(DEFAULT_ORDER);
  const [searchQueryInputValue, searchQuery, setSearchQuery] =
    useDebouncedState("");

  const projectId = useCurrentProjectId();

  const { pages, isLoading, fetchNextPage, hasNextPage } = useInfinitePages({
    projectId: projectId ?? null,
    search: searchQuery,
    limit: 6,
    orderBy: order,
  });

  const noPages = pages?.length === 0 && !isLoading && searchQuery.length === 0;
  const emptySearchResults =
    pages?.length === 0 && !isLoading && searchQuery.length > 0;

  if (noPages) {
    return (
      <EmptyState
        title="No pages created yet"
        size="base"
        description="Start building with chat or by using one of our ready-made templates."
        image={
          <img
            src="/images/empty-pages.svg"
            alt="no pages"
            className="h-16 w-24"
          />
        }
        actionButtonProps={{
          variant: "secondary",
          size: "base",
          endEnhancer: <ArrowRight size={12} />,
          children: "Templates",
          onClick: () => {
            setSelectedTab("templates");
          },
        }}
        UNSAFE_className="w-full h-full"
        variant="noRings"
      />
    );
  }

  return (
    <div className="flex gap-4 flex-col h-full">
      <div className="grid grid-cols-[1fr_auto] items-center gap-2 sm:flex sm:gap-3 sm:items-center w-full flex-shrink-0">
        <div className="flex min-w-0 w-full">
          <Input
            startEnhancer={<Search size={12} />}
            placeholder="Search"
            size="base"
            layoutClassName="grow"
            value={searchQueryInputValue}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <ResponsiveControls variant="secondary" />
        <div className="col-span-2 sm:col-span-1 sm:flex-1">
          <SortDropdown order={order} onOrderChange={setOrder} />
        </div>
      </div>
      {emptySearchResults ? (
        <EmptyState
          title="No pages found"
          description="Start building with Replo — we can't wait to bring your ideas to life!"
          icon={<FolderSearch className="icon-size-default" />}
          UNSAFE_className="py-[10%]"
        />
      ) : (
        <InfiniteScroll
          dataLength={pages.length}
          next={() => void fetchNextPage()}
          hasMore={hasNextPage}
          loader={
            <div
              className="grid gap-4 mt-4"
              style={{
                gridTemplateColumns: `repeat(auto-fill, minmax(${GRID_CONSTANTS.MIN_CARD_WIDTH}px, 1fr))`,
              }}
            >
              {Array.from({ length: 3 }).map((_, index) => (
                <PageCardSkeleton key={`loader-${index}`} />
              ))}
            </div>
          }
          scrollableTarget="home-scroll-container"
        >
          <div
            className="grid gap-4 pt-4 pb-6"
            style={{
              gridTemplateColumns: `repeat(auto-fill, minmax(${GRID_CONSTANTS.MIN_CARD_WIDTH}px, 1fr))`,
            }}
          >
            {isLoading
              ? Array.from({ length: 9 }).map((_, index) => (
                  <PageCardSkeleton key={`initial-${index}`} />
                ))
              : pages.map((file) => <PageCard key={file.id} file={file} />)}
          </div>
        </InfiniteScroll>
      )}
    </div>
  );
}

export default PagesSection;
